import FormErrors from "@patient-watch/forms/FormErrors";
import {
    searchForTemplateAiWizardNavigation,
    useTemplateAiWizardSearch,
} from "@/features/ai-builder/useTemplateAiDraftStore";
import * as c from "@patient-watch/ui";
import ShockwaveDetailsFormFields from "@/features/shockwave/components/ShockwaveDetailsFormFields";
import { LockableField, LockableSectionTitle } from "@/features/templates/components/TemplateScopeLock";
import { TemplateStepFooter } from "@/features/templates/components/TemplateStepFooter";
import { useTemplateNavigationStore } from "@/features/templates/hooks/useTemplateNavigationStore";
import { useFinalizeTemplateWizard } from "@/features/templates/hooks/useFinalizeTemplateWizard";
import { useTemplatesCopy } from "@/features/i18n/templatesCopy";
import { services as s } from "@/services";
import { templateSettingsSchema } from "@patient-watch/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "@patient-watch/ui/sonner";
import { useToastCopy } from "@/features/i18n/toastCopy";
import type { z } from "zod";
import { useAuth } from "@/utils/auth";

interface RouteParams {
    shockwaveDetailId: string;
    templateId: string;
}

type ShockwaveDetailsValues = Pick<z.infer<typeof templateSettingsSchema>, "shockwave_details">;

const ShockwaveDetailsFormFieldsPage = () => {
    const auth = useAuth();
    const toastCopy = useToastCopy();
    const templatesCopy = useTemplatesCopy();
    const { templateId } = Route.useParams();
    const search = useTemplateAiWizardSearch(templateId, Route.useSearch());
    const shockwave_id = search.shockwave_id;
    const shockwave_details = search.shockwave_details;
    const { markStepComplete, setCurrentStep, getNextStepRoute, getPreviousStepRoute, currentStep } =
        useTemplateNavigationStore();
    const navigate = useNavigate();
    const { finalize, isSubmitting, isTemplateOwnerLookupBlockingSubmit } = useFinalizeTemplateWizard(
        templateId,
        search
    );
    const form = useForm<ShockwaveDetailsValues>({
        resolver: zodResolver(
            templateSettingsSchema.pick({
                shockwave_details: true,
            })
        ),
        defaultValues: {
            shockwave_details: shockwave_details ?? [
                {
                    shockwave_id: shockwave_id || undefined,
                    shockwave_detail_id: s.uuidv4(),
                    side: "",
                    orthopaedic_area: "",
                    site: "",
                    subsite: "",
                    shockwave_type: "",
                    dose: "",
                    unit: "",
                    impulses: "",
                },
            ],
        },
    });

    useEffect(() => {
        setCurrentStep("shockwave-details");
    }, [setCurrentStep]);

    const onSubmit = async (data: ShockwaveDetailsValues) => {
        try {
            markStepComplete(currentStep);
            const nextRoute = getNextStepRoute(currentStep, { templateId });
            if (nextRoute) {
                const nextSettings = { ...search, ...data };
                const wizardSearchPayload = searchForTemplateAiWizardNavigation(
                    templateId,
                    auth?.id ?? "",
                    nextSettings
                );
                if (!wizardSearchPayload) return;
                navigate({
                    ...nextRoute,
                    search: wizardSearchPayload,
                });
            } else {
                finalize(data);
            }
        } catch (error) {
            toast.error(toastCopy.failedSaveShockwaveDetails);
            console.error("Form submission error:", error);
        }
    };

    const handlePrevious = () => {
        const prevRoute = getPreviousStepRoute(currentStep, { templateId });
        if (prevRoute) {
            const nextSettings = { ...search, ...form.getValues() };
            const wizardSearchPayload = searchForTemplateAiWizardNavigation(templateId, auth?.id ?? "", nextSettings);
            if (!wizardSearchPayload) return;
            navigate({
                ...prevRoute,
                search: wizardSearchPayload,
            });
        }
    };

    return (
        <>
            <c.ui.Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="w-full form">
                    <LockableField field="shockwave_details">
                        <LockableSectionTitle
                            title="Shockwave Details"
                            description="Add details about each shockwave treatment site"
                        />
                        <ShockwaveDetailsFormFields shockwaveId={shockwave_id ?? ""} />
                    </LockableField>

                    <FormErrors />

                    <TemplateStepFooter
                        onBack={handlePrevious}
                        submitPrimary
                        primaryAction="save"
                        primaryLabel={templatesCopy.saveTemplate}
                        isLoadingPrimary={isSubmitting}
                        disabledPrimary={isTemplateOwnerLookupBlockingSubmit}
                    />
                </form>
            </c.ui.Form>
        </>
    );
};

const shockwaveDetailsSearchSchema = templateSettingsSchema.partial().pick({
    shockwave_id: true,
    shockwave_details: true,
});

export const Route = createFileRoute("/_app/templates/$templateId/events/shockwave/details")({
    component: ShockwaveDetailsFormFieldsPage,
    validateSearch: (search) => shockwaveDetailsSearchSchema.parse(search),
    loader: ({ params }: { params: RouteParams }) => {
        return {
            shockwaveDetailId: params.shockwaveDetailId,
        };
    },
});
